import styled from '@emotion/styled';
import { getImage } from '@utils/imageHelper';
import Typography from 'aquilla/core/atoms/Typography';

import { mediaQuery, Spacings } from '@styles/index';
import { useContext } from 'react';
import { DoubtsAIContext } from '@context/DoubtsAIContext';
import Image from 'next/image';
import boxShadowGenerator from '@utils/boxShadowGenerator';
import { useMediaQuery } from '@material-ui/core';
import { EMPTY_CHAT_TEXT } from './constants';
import DoubtSolution from './DoubtSolution/DoubtSolution';
import useDoubtSolution from './hooks/useDoubtSolution';
import { ContentType } from './types';

const ChatWrapper = styled.div`
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  padding: 16px;
`;

const MessageContainer = styled.div<{ isUser: boolean }>`
  display: flex;
  justify-content: ${(props) => (props.isUser ? 'flex-end' : 'flex-start')};
`;

const MessageBubble = styled.div<{ isUser: boolean; isImgSolution?: boolean }>`
  width: ${({ isUser }) => (isUser ? '380px' : '100%')};
  padding: 12px 14px;
  padding-bottom: ${({ isImgSolution }) => (isImgSolution ? '0' : '12px')};
  border-radius: 10px;
  background: ${(props) =>
    props.isUser ? 'var(--color-divider)' : 'transparent'};
  color: var(--color-base-fill);
  position: relative;

  ${mediaQuery.uptoMobile} {
    max-width: ${({ isUser }) => (isUser ? '280px' : '100%')};
  }
`;

const MessageDoubtSolutionPlayerWrapper = styled.div`
  width: 100%;
  background: var(--color-base-0);
  border-radius: ${Spacings.SPACING_2B} ${Spacings.SPACING_2B} 0px 0px;
  box-shadow: ${boxShadowGenerator()};
  padding: 12px;
  display: flex;
  gap: 8px;
  flex-direction: column;
  ${mediaQuery.uptoMobile} {
    border-radius: ${Spacings.SPACING_1B};
    height: 450px;
  }
`;

const VideoSolutionInfoText = styled.span`
  font-size: 14px;
  color: var(--color-text-secondary);
  font-weight: 400;
`;

const MessageImage = styled.img`
  border-radius: 4px;
  max-height: 300px;
  overflow: hidden;
  object-fit: contain;
`;

const Timestamp = styled(Typography)`
  font-size: 12px;
  text-align: right;
  margin-top: 4px;
  color: var(--color-text-secondary);
`;

const VerticalDivider = styled.div`
  height: 24px;
`;

const StyledSubTitle = styled(Typography)`
  font-size: 20px;
`;

const StyledMessageText = styled(Typography)`
  white-space: pre-line;
`;

const EmptyChatContainer = () => {
  return (
    <div style={{ textAlign: 'center' }}>
      <Image
        width={116}
        height={116}
        alt="intro"
        src={getImage('unacademy-ai/unacademy-ai-intro.svg')}
      />
      <StyledSubTitle variant="h5" color="var(--color-text-primary)">
        {EMPTY_CHAT_TEXT}
      </StyledSubTitle>
    </div>
  );
};

const MessageText = ({ text }) => {
  return <span>{text}</span>;
};

const renderChatContent = (
  contentType: ContentType,
  text?: string,
  image?: string
) => {
  if (contentType === ContentType.TEXT) {
    return <MessageText text={text} />;
  }
  if (contentType === ContentType.IMAGE) {
    return <MessageImage width="100%" src={image} alt="doubt image" />;
  }
};

const formatTime = (timestamp: string | number | Date) => {
  return new Date(timestamp).toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
};
const Chats = ({ goalUID }: { goalUID: string }) => {
  const { chats, doubtsUID, submittedImageUrl, wrapperContainerRef } =
    useContext(DoubtsAIContext);
  const isMobile = useMediaQuery(mediaQuery.uptoMobile);
  const {
    doubtSolutionResult,
    liveClassData,
    selectedSolutionUID,
    onPlayerReady,
    recordingURL
  } = useDoubtSolution({
    doubtUID: doubtsUID,
    goalUID
  });
  if (chats.length === 0 && !submittedImageUrl && !doubtsUID) {
    return <EmptyChatContainer />;
  }
  return (
    <ChatWrapper ref={wrapperContainerRef}>
      {submittedImageUrl && doubtsUID ? (
        <MessageContainer isUser>
          <MessageBubble isUser>
            {renderChatContent(ContentType.IMAGE, undefined, submittedImageUrl)}
            <Timestamp variant="p3">{formatTime(new Date())}</Timestamp>
          </MessageBubble>
        </MessageContainer>
      ) : null}
      {chats
        .filter((chat) => chat.isDoubtImageResponse)
        .map((msg) => (
          <MessageContainer
            key={msg.id + msg.sender}
            isUser={msg.sender === 'user'}
          >
            <MessageBubble
              isUser={msg.sender === 'user'}
              isImgSolution={!!(doubtSolutionResult === 'success' && doubtsUID)}
            >
              <StyledMessageText variant="p1">{msg.text}</StyledMessageText>
            </MessageBubble>
          </MessageContainer>
        ))}
      {doubtSolutionResult === 'success' && doubtsUID && !isMobile && (
        <>
          <MessageContainer isUser={false}>
            <MessageDoubtSolutionPlayerWrapper>
              <VideoSolutionInfoText>
                Here is a video from an Unacademy educator with the solution to
                your question
              </VideoSolutionInfoText>
              <DoubtSolution
                isSolutionAvailable={doubtSolutionResult === 'success'}
                liveClassData={liveClassData}
                goalUID={goalUID}
                doubtUID={doubtsUID}
                onPlayerReady={onPlayerReady}
                recordingURL={recordingURL}
                selectedSolutionUID={selectedSolutionUID}
              />
            </MessageDoubtSolutionPlayerWrapper>
          </MessageContainer>
        </>
      )}
      {chats
        .filter((chat) => !chat.isDoubtImageResponse && chat.text)
        .map((msg) => (
          <MessageContainer
            key={msg.id + msg.sender}
            isUser={msg.sender === 'user'}
          >
            <MessageBubble isUser={msg.sender === 'user'}>
              <StyledMessageText variant="p1">{msg.text}</StyledMessageText>
              <VerticalDivider />
              {msg.sender === 'user' && (
                <div style={{ textAlign: 'right' }}>
                  <Timestamp variant="p3">
                    {formatTime(msg.timestamp)}
                  </Timestamp>
                </div>
              )}
            </MessageBubble>
          </MessageContainer>
        ))}
    </ChatWrapper>
  );
};

export default Chats;
